import React, { useMemo, useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { MdClose } from 'react-icons/md';
import { toast } from 'react-toastify';
import { useAuth } from '../../Context/AuthContext';
import { getAllProviderSettings } from '../../Services/ProviderService';
import SearchableDropdown from '../Subscription/SearchableDropdown';
import AddressSelectorFields from '../Common/AddressSelectorFields';
import DocumentUpload from '../Common/DocumentUpload';
import CustomDatePicker from '../Common/CustomDatePicker';


const SETTING_TYPE_IDS = {
  office_type: 24,
  employer_type: 26,
  staff_category: 25,
  employer_in: 27,
  patient_age_range: 28,
  patient_gender: 29,
  patient_type: 30,
};

const mapProviderSettingItem = (item) => ({
  value: item.id ?? item.Id ?? item.value ?? item.value_id ?? null,
  label: item.name ?? item.Name ?? item.code ?? String(item.id ?? ''),
});



const YES_NO_OPTIONS = [
  { id: true, label: 'Yes' },
  { id: false, label: 'No' },
];

const validationSchema = Yup.object({
  employer_name: Yup.string().trim().required('Practice/Employer name is required'),
  office_type: Yup.number().nullable().required('Office type is required'),
  current_position_held: Yup.string().trim().nullable(),
  employer_type: Yup.number().nullable().typeError('Must be a valid number'),
  start_date: Yup.date().nullable().typeError('Invalid date format'),
  end_date: Yup.date().nullable().typeError('Invalid date format'),
  staff_category: Yup.number().nullable().typeError('Must be a valid number'),
  employer_in: Yup.number().nullable().typeError('Must be a valid number'),
  patient_age_range: Yup.number().nullable().typeError('Must be a valid number'),
  patient_type: Yup.number().nullable().typeError('Must be a valid number'),
  patient_gender: Yup.number().nullable().typeError('Must be a valid number'),
  accept_new_patient: Yup.boolean().nullable(),
  case_load: Yup.string().trim().nullable(),
  contact: Yup.object({
    contact_name: Yup.string().trim().nullable(),
    contact_title: Yup.string().trim().nullable(),
    contact_email: Yup.string().trim().nullable().email('Invalid email'),
    contact_phone: Yup.string().trim().nullable(),
    contact_fax: Yup.string().trim().nullable(),
  }).nullable(),
  address: Yup.object({
    street: Yup.string().trim().nullable(),
    street2: Yup.string().trim().nullable(),
    city: Yup.string().trim().nullable(),
    state: Yup.string().trim().nullable(),
    country: Yup.string().trim().nullable(),
    zipcode: Yup.string().trim().nullable(),
  }).nullable(),
  w9_practice_name: Yup.string().trim().nullable(),
  tax_id: Yup.string().trim().nullable(),
  npi: Yup.string().trim().nullable(),
  group_medicaid: Yup.string().trim().nullable(),
  group_medicare: Yup.string().trim().nullable(),
});

const emptyForm = {
  id: null,
  provider_id: null,
  employer_name: '',
  office_type: null,
  current_position_held: '',
  employer_type: null,
  start_date: null,
  end_date: null,
  staff_category: null,
  employer_in: null,
  patient_age_range: null,
  patient_type: null,
  patient_gender: null,
  accept_new_patient: false,
  case_load: '',
  document: null,
  contact_id: null,
  contact: { contact_name: '', contact_title: '', contact_email: '', contact_phone: '', contact_fax: '' },
  address_id: null,
  address: { street: '', street2: '', city: '', state: '', country: '', zipcode: '' },
  w9_practice_name: '',
  tax_id: '',
  npi: '',
  group_medicaid: '',
  group_medicare: '',
};

export default function PracticeEmployerModal({ isOpen, onClose, onSave, initialData, isLoading }) {
  const { token } = useAuth();
  const [providerSettings, setProviderSettings] = useState({
    office_type: [],
    employer_type: [],
    staff_category: [],
    employer_in: [],
    patient_age_range: [],
    patient_type: [],
    patient_gender: [],
  });
  const [settingsLoading, setSettingsLoading] = useState(false);

  const loadProviderSettings = async () => {
    if (!token) return;
    setSettingsLoading(true);

    try {
      const entries = Object.entries(SETTING_TYPE_IDS);
      const results = await Promise.all(
        entries.map(async ([key, typeId]) => {
          try {
            const response = await getAllProviderSettings(token, typeId);
            if (response?.is_success) {
              return {
                key,
                options: Array.isArray(response.data) ? response.data.map(mapProviderSettingItem) : [],
              };
            } else if(response?.is_success === false && response?.status_code === 500) {
              toast.error("Internal server Error. Please Contact Support Team.");
            } else {
              toast.error(response?.message || `Failed to load settings`);
              console.error(`Failed to load ${key} settings:`, response?.message || 'Unknown error');
            }
          } catch (error) {
            console.error(`Failed to load ${key} settings:`, error);
          }
          return { key, options: [] };
        })
      );
      setProviderSettings(results.reduce((acc, item) => ({ ...acc, [item.key]: item.options }), {}));
    } finally {
      setSettingsLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    loadProviderSettings();
  }, [token]);

  const initialValues = useMemo(() => {
    if (!initialData) return emptyForm;
    
    const merged = { ...emptyForm, ...initialData };
    merged.document = merged.document || merged.file_name || merged.fileName || null;
    
    // Convert numeric fields to numbers to match validation schema
    const numericFields = ['office_type', 'employer_type', 'staff_category', 'employer_in', 'patient_age_range', 'patient_type', 'patient_gender'];
    numericFields.forEach(field => {
      if (merged[field] !== null && merged[field] !== undefined) {
        merged[field] = Number(merged[field]);
      }
    });
    
    return merged;
  }, [initialData]);

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      const payload = {
        ...values,
        id: values.id || null,
        office_type: values.office_type !== '' && values.office_type !== null && values.office_type !== undefined ? Number(values.office_type) : null,
        employer_type: values.employer_type !== '' && values.employer_type !== null && values.employer_type !== undefined ? Number(values.employer_type) : null,
        staff_category: values.staff_category !== '' && values.staff_category !== null && values.staff_category !== undefined ? Number(values.staff_category) : null,
        employer_in: values.employer_in !== '' && values.employer_in !== null && values.employer_in !== undefined ? Number(values.employer_in) : null,
        patient_age_range: values.patient_age_range !== '' && values.patient_age_range !== null && values.patient_age_range !== undefined ? Number(values.patient_age_range) : null,
        patient_type: values.patient_type !== '' && values.patient_type !== null && values.patient_type !== undefined ? Number(values.patient_type) : null,
        patient_gender: values.patient_gender !== '' && values.patient_gender !== null && values.patient_gender !== undefined ? Number(values.patient_gender) : null,
        accept_new_patient: values.accept_new_patient === true || values.accept_new_patient === 'true' ? true : values.accept_new_patient === false || values.accept_new_patient === 'false' ? false : null,
        start_date: values.start_date || null,
        end_date: values.end_date || null,
        type: values.id ? 'update' : 'create',
      };
      onSave(payload);
    },
  });

  const handleDropdownChange = (field) => (value) => {
    const numValue = value !== null && value !== undefined && value !== '' ? Number(value) : null;
    formik.setFieldValue(field, numValue, true);
    formik.setFieldTouched(field, true, false);
  };

  if (!isOpen) return null;

  const renderAsterisk = () => <span className="text-danger">*</span>;

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(var(--hc-black-rgb), 0.5)' }}>
      <div className="modal-dialog modal-xl modal-dialog-centered">
        <div className="modal-content border-0 rounded-4">
          <div className="modal-header border-bottom-0 px-4 py-2 d-flex justify-content-between align-items-center">
            <h5 className="modal-title fw-bold hc-text-primary m-0">{initialData?.id ? 'Edit Employer' : 'Add Employer'}</h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={isLoading}>
            </button>
          </div>

          <form onSubmit={formik.handleSubmit}>
            <div className="modal-body px-4 py-2">
              <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                <h6 className="fw-bold hc-text-primary mb-3">Basic Information</h6>
                <div className="row g-3">
                  <div className="col-md-12 col-lg-6">
                    <label className="form-label fw-semibold">Current Practice/Employer Name {renderAsterisk()}</label>
                    <input
                      {...formik.getFieldProps('employer_name')}
                      className={`form-control rounded-3 shadow-sm ${formik.touched.employer_name && formik.errors.employer_name ? 'is-invalid' : ''}`}
                      placeholder="Enter practice or employer name"
                    />
                    <div className="invalid-feedback">{formik.errors.employer_name}</div>
                  </div>

                  <div className="col-md-12 col-lg-6">
                    <label className="form-label fw-semibold">Current Position Held</label>
                    <input {...formik.getFieldProps('current_position_held')} className="form-control rounded-3 shadow-sm" />
                  </div>

                  <div className="col-md-12 col-lg-6">
                    <SearchableDropdown
                      label="Office Type"
                      required={true}
                      placeholder="Select Option"
                      options={providerSettings.office_type}
                      value={formik.values.office_type}
                      onChange={handleDropdownChange('office_type')}
                      disabled={settingsLoading}
                      loading={settingsLoading}
                      className={formik.touched.office_type && formik.errors.office_type ? 'is-invalid' : ''}
                      emptyMessage="No office types found"
                    />
                    {formik.touched.office_type && formik.errors.office_type ? (
                      <div className="invalid-feedback d-block">{formik.errors.office_type}</div>
                    ) : null}
                  </div>

                  <div className="col-md-12 col-lg-6">
                    <SearchableDropdown
                      label="Practice/Employer Type"
                      placeholder="Select Option"
                      options={providerSettings.employer_type}
                      value={formik.values.employer_type}
                      onChange={handleDropdownChange('employer_type')}
                      disabled={settingsLoading}
                      loading={settingsLoading}
                      emptyMessage="No employer types found"
                    />
                  </div>
                </div>
              </div>

              <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                <h6 className="fw-bold hc-text-primary mb-3">Timeline & Categories</h6>
                <div className="row g-3">
                  <div className="col-md-6 col-lg-3">
                    <label className="form-label fw-semibold">Start Date</label>
                    {/* <input type="date" {...formik.getFieldProps('start_date')} className="form-control rounded-3" /> */}

                    <CustomDatePicker
                      name="start_date"
                      formik={formik}
                    />
                  </div>

                  <div className="col-md-6 col-lg-3">
                    <label className="form-label fw-semibold">End Date</label>
                    {/* <input type="date" {...formik.getFieldProps('end_date')} className="form-control rounded-3" /> */}
                    <CustomDatePicker
                      name="end_date"
                      formik={formik}
                    />
                  </div>


                  <div className="col-md-12 col-lg-3">
                    <SearchableDropdown
                      label="Staff Category"
                      placeholder="Select Option"
                      options={providerSettings.staff_category}
                      value={formik.values.staff_category}
                      onChange={handleDropdownChange('staff_category')}
                      disabled={settingsLoading}
                      loading={settingsLoading}
                      emptyMessage="No staff categories found"
                    />
                  </div>

                  <div className="col-md-12 col-lg-3">
                    <SearchableDropdown
                      label="Practice/Employer in"
                      placeholder="Select Option"
                      options={providerSettings.employer_in}
                      value={formik.values.employer_in}
                      onChange={handleDropdownChange('employer_in')}
                      disabled={settingsLoading}
                      loading={settingsLoading}
                      emptyMessage="No listing options found"
                    />
                  </div>
                </div>
              </div>

              <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                <h6 className="fw-bold hc-text-primary mb-3">Patient / Service Info</h6>
                <div className="row g-3">
                  <div className="col-md-6 col-lg-3">
                    <SearchableDropdown
                      label="Patient Age Range"
                      placeholder="Select Option"
                      options={providerSettings.patient_age_range}
                      value={formik.values.patient_age_range}
                      onChange={handleDropdownChange('patient_age_range')}
                      disabled={settingsLoading}
                      loading={settingsLoading}
                      emptyMessage="No age ranges found"
                    />
                  </div>

                  <div className="col-md-6 col-lg-3">
                    <SearchableDropdown
                      label="Patient Type"
                      placeholder="Select Option"
                      options={providerSettings.patient_type}
                      value={formik.values.patient_type}
                      onChange={handleDropdownChange('patient_type')}
                      disabled={settingsLoading}
                      loading={settingsLoading}
                      emptyMessage="No patient types found"
                    />
                  </div>

                  <div className="col-md-6 col-lg-3">
                    <SearchableDropdown
                      label="Patient Gender"
                      placeholder="Select Option"
                      options={providerSettings.patient_gender}
                      value={formik.values.patient_gender}
                      onChange={handleDropdownChange('patient_gender')}
                      disabled={settingsLoading}
                      loading={settingsLoading}
                      emptyMessage="No genders found"
                    />
                  </div>

                  <div className="col-md-6 col-lg-3">
                    <label className="form-label fw-semibold">New patients?</label>
                    <select
                      name="accept_new_patient"
                      className="form-select rounded-3"
                      value={String(formik.values.accept_new_patient)}
                      onChange={(e) => formik.setFieldValue('accept_new_patient', e.target.value === 'true')}
                    >
                      <option value="">Select Option</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>

                  <div className="col-md-12 mt-2">
                    <label className="form-label fw-semibold">Case Load</label>
                    <input {...formik.getFieldProps('case_load')} className="form-control rounded-3" />
                  </div>
                </div>
              </div>

              <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                <h6 className="fw-bold hc-text-primary mb-3">Document</h6>
                <DocumentUpload
                  id="practice-employer-document-input"
                  label="Upload Document"
                  value={formik.values.document}
                  onChange={(file) => formik.setFieldValue('document', file)}
                />
              </div>

              <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                <h6 className="fw-bold hc-text-primary mb-3">Contact Information</h6>
                <div className="row g-3">
                  <div className="col-md-6 col-lg-3">
                    <label className="form-label fw-semibold">Contact Name</label>
                    <input {...formik.getFieldProps('contact.contact_name')} className="form-control rounded-3" />
                  </div>

                  <div className="col-md-6 col-lg-3">
                    <label className="form-label fw-semibold">Contact Title</label>
                    <input {...formik.getFieldProps('contact.contact_title')} className="form-control rounded-3" />
                  </div>

                  <div className="col-md-6 col-lg-3">
                    <label className="form-label fw-semibold">Contact Email</label>
                    <input {...formik.getFieldProps('contact.contact_email')} className="form-control rounded-3" />
                  </div>

                  <div className="col-md-6 col-lg-3">
                    <label className="form-label fw-semibold">Contact Phone</label>

                    
                  
                    <input {...formik.getFieldProps('contact.contact_phone')} className="form-control rounded-3" />
                  </div>

                  <div className="col-md-12 col-lg-3">
                    <label className="form-label fw-semibold">Contact Fax</label>
                    <input {...formik.getFieldProps('contact.contact_fax')} className="form-control rounded-3" />
                  </div>
                </div>
              </div>

              <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                <h6 className="fw-bold hc-text-primary mb-3">Address Information</h6>
                <div className="row g-3">
                  <div className="col-12">
                    <AddressSelectorFields
                      title="Address"
                      fieldPrefix="address"
                      values={formik.values.address}
                      errors={formik.errors.address || {}}
                      touched={formik.touched.address || {}}
                      onFieldChange={(fieldName, value) => formik.setFieldValue(fieldName, value)}
                    />
                  </div>
                </div>
              </div>

              <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                <h6 className="fw-bold hc-text-primary mb-3">Billing & Identifiers</h6>
                <div className="row g-3">
                  <div className="col-md-6 col-lg-4">
                    <label className="form-label fw-semibold">W9 Practice Name</label>
                    <input {...formik.getFieldProps('w9_practice_name')} className="form-control rounded-3" />
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <label className="form-label fw-semibold">Tax ID</label>
                    <input {...formik.getFieldProps('tax_id')} className="form-control rounded-3" />
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <label className="form-label fw-semibold">NPI</label>
                    <input {...formik.getFieldProps('npi')} className="form-control rounded-3" />
                  </div>

                  <div className="col-md-6 mt-2">
                    <label className="form-label fw-semibold">Group Medicaid</label>
                    <input {...formik.getFieldProps('group_medicaid')} className="form-control rounded-3" />
                  </div>

                  <div className="col-md-6 mt-2">
                    <label className="form-label fw-semibold">Group Medicare</label>
                    <input {...formik.getFieldProps('group_medicare')} className="form-control rounded-3" />
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer border-top-0 p-4 d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-light rounded-3 px-4" onClick={onClose} disabled={isLoading}>Cancel</button>
              <button type="submit" className="btn hc-bg-primary text-white rounded-3 px-4" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
